/* @flow */
import React, { useEffect } from 'react';
import { Alert, Form, Icon, Input, Popover, Progress } from 'antd';
import { useImmer } from 'use-immer';
import { SubmitButton } from '@/components';
import { isPhoneValid } from '@/utils/validation';
import { querystring } from '@/utils/common';
import styles from './SignUp.module.pcss';

const { Item } = Form;

type Props = {
  form: {
    getFieldValue: Function,
    validateFields: Function,
    getFieldDecorator: Function,
  },
  auth: AuthT,
  signUp: (credential: Object) => void,
  token: ?string,
  location: {
    search: string,
  },
  fetchTokenData: (token: string) => Promise<any>,
  tokenData: Object,
};

type State = {
  confirmDirty: boolean,
  visible: boolean,
  help: string,
  countryCode: string,
};

const passwordStatusMap = {
  ok: <div className={styles.success}>strong</div>,
  pass: <div className={styles.warning}>medium</div>,
  poor: <div className={styles.error}>short</div>,
};

const passwordProgressMap = {
  ok: 'success',
  pass: 'normal',
  poor: 'exception',
};

const SignUp = ({ fetchTokenData, form, signUp, auth, location, tokenData }: Props) => {
  const [state, setState] = useImmer<State>({
    confirmDirty: false,
    visible: false,
    countryCode: '+1',
    help: '',
    token: querystring('token', location.search),
  });

  useEffect(() => {
    if (state.token) {
      fetchTokenData(state.token);
    }
  }, [fetchTokenData, state.token]);

  const getPasswordStatus = () => {
    const value = form.getFieldValue('password');
    if (value && value.length > 9) {
      return 'ok';
    }
    if (value && value.length > 5) {
      return 'pass';
    }
    return 'poor';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    form.validateFields({ force: true }, (errors, values) => {
      handleConfirmBlur();
      if (!errors) {
        const { firstName, lastName, phone, ...rest } = values;
        signUp({
          ...rest,
          first_name: firstName,
          last_name: lastName,
          phone_number: String.prototype.concat(state.countryCode, phone),
        });
      }
    });
  };

  const handleConfirmBlur = () => {
    setState((draft) => {
      draft.visible = false;
    });
  };

  const checkPassword = (rule, value, callback) => {
    if (!value) {
      setState((draft) => {
        draft.help = 'Required';
        draft.visible = !!value;
      });
      callback('Error');
    } else {
      setState((draft) => {
        draft.help = '';
      });
      if (!state.visible) {
        setState((draft) => {
          draft.visible = !!value;
        });
      }
      if (value.length < 6) {
        callback('error');
      } else {
        if (state.value && state.confirmDirty) {
          form.validateFields(['confirm'], { force: true });
        }
        callback();
      }
    }
  };

  const validatePhone = (rule, value, callback) => {
    if (value && !isPhoneValid(value)) {
      callback('Wrong format');
    } else {
      callback();
    }
  };

  const renderPasswordProgress = () => {
    const value = form.getFieldValue('password');
    const passwordStatus = getPasswordStatus();
    return value && value.length ? (
      <div className={styles[`progress-${passwordStatus}`]}>
        <Progress
          status={passwordProgressMap[passwordStatus]}
          className={styles.progress}
          strokeWidth={6}
          percent={value.length * 10 > 100 ? 100 : value.length * 10}
          showInfo={false}
        />
      </div>
    ) : null;
  };

  return (
    <div className={styles.root}>
      <Form onSubmit={handleSubmit}>
        <Item className={styles.names}>
          <Item className={styles.firstName}>
            {form.getFieldDecorator('firstName', {
              rules: [
                {
                  required: true,
                  message: 'Required',
                },
                {
                  type: 'string',
                },
              ],
            })(<Input prefix={<Icon type="user" />} size="large" placeholder="First Name" />)}
          </Item>
          <Item className={styles.lastName}>
            {form.getFieldDecorator('lastName', {
              rules: [
                {
                  required: true,
                  message: 'Required',
                },
                {
                  type: 'string',
                },
              ],
            })(<Input prefix={<Icon type="user" />} size="large" placeholder="Last Name" />)}
          </Item>
        </Item>
        <Item>
          {form.getFieldDecorator('organization', {
            rules: [
              {
                required: true,
                message: 'Required',
              },
              {
                type: 'string',
                message: 'Wrong format',
              },
            ],
            initialValue: tokenData?.organization?.name,
          })(
            <Input
              prefix={<Icon type="bank" />}
              size="large"
              placeholder="Company Name"
              disabled={!!tokenData?.organization?.name}
            />
          )}
        </Item>
        <Item>
          {form.getFieldDecorator('phone', {
            rules: [
              { required: true, message: 'Please enter your phone number.' },
              {
                validator: validatePhone,
              },
            ],
          })(
            <Input
              addonBefore={state.countryCode}
              prefix={<Icon type="mobile" />}
              placeholder="Phone Number (ex. 1234567890)"
              size="large"
            />
          )}
        </Item>
        <Item display-if={auth.registerErrors.phone_number}>
          <Alert message={auth.registerErrors.phone_number[0]} type="error" />
        </Item>
        <Item>
          {form.getFieldDecorator('email', {
            rules: [
              {
                required: true,
                message: 'Required',
              },
              {
                type: 'email',
                message: 'This email address is formatted incorrectly.',
              },
            ],
            initialValue: tokenData?.email,
          })(<Input prefix={<Icon type="mail" />} size="large" placeholder="Enter your company email" />)}
        </Item>
        <Item display-if={auth.registerErrors.email}>
          <Alert message={auth.registerErrors.email[0]} type="error" />
        </Item>
        <Item help={state.help}>
          <Popover
            getPopupContainer={(node) => node.parentNode}
            content={
              <div style={{ padding: '4px 0' }}>
                {passwordStatusMap[getPasswordStatus()]}
                {renderPasswordProgress()}
                <div style={{ marginTop: 10 }}>
                  {`Please enter at least 8 characters and don't use passwords that are easy to guess.`}
                </div>
              </div>
            }
            overlayStyle={{ width: 240 }}
            placement="top"
            visible={state.visible}
          >
            {form.getFieldDecorator('password', {
              rules: [
                {
                  validator: checkPassword,
                },
              ],
            })(
              <Input.Password
                prefix={<Icon type="lock" />}
                size="large"
                onBlur={handleConfirmBlur}
                placeholder="Create your password"
              />
            )}
          </Popover>
        </Item>
        <Item display-if={auth.registerErrors.non_field_errors}>
          <Alert message={auth.registerErrors.non_field_errors[0]} type="error" />
        </Item>
        <SubmitButton loading={auth.isSignUp} name="Sign me up" className={styles.submit} />
      </Form>
    </div>
  );
};

export default Form.create({ name: 'signUpForm' })(SignUp);
